import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  MenuItem,
  IconButton,
  Menu,
  Avatar,
  Box,
  Badge,
} from "@material-ui/core";
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Brightness4 as DarkModeIcon,
  BrightnessHigh as LightModeIcon,
} from "@material-ui/icons";

import MainListItems from "./MainListItems";
import SearchBar from "../components/SearchBar";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";

const drawerWidth = 260; // Gxon uses 260px

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    backgroundColor: theme.palette.background.default,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  toolbar: {
    paddingRight: 24,
    paddingLeft: 24,
    minHeight: 70, // Gxon header height
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  menuButton: {
    marginRight: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  menuButtonHidden: {
    display: "none",
  },
  searchContainer: {
    flexGrow: 1,
    maxWidth: 400,
  },
  toolbarActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  iconButton: {
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.mode === "light" ? "#F3F4F6" : "#374151",
    },
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: theme.palette.background.paper,
    borderRight: "none",
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
    overflowX: "hidden",
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    minHeight: 70,
    justifyContent: "space-between",
  },
  logo: {
    height: 32,
    width: "auto",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    overflow: "auto",
    backgroundColor: theme.palette.background.default,
    marginTop: 70, // Header height
  },
  userMenu: {
    marginTop: theme.spacing(1),
  },
  userMenuItem: {
    padding: theme.spacing(1.5, 2),
    fontSize: "0.875rem",
  },
  avatar: {
    width: 36,
    height: 36,
    backgroundColor: theme.palette.primary.main,
    fontSize: "0.875rem",
    fontWeight: 600,
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (document.body.offsetWidth > 960) {
      setDrawerOpen(true);
    } else {
      setDrawerOpen(false);
    }
  }, []);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  if (loading) {
    return <BackdropLoading />;
  }

  const userInitials = user?.name
    ? user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2)
    : "U";

  return (
    <div className={classes.root}>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        elevation={0}
      >
        <Toolbar className={classes.toolbar}>
          <IconButton
            edge="start"
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
            onClick={() => setDrawerOpen(!drawerOpen)}
          >
            <MenuIcon />
          </IconButton>

          {/* Search Bar */}
          <div className={classes.searchContainer}>
            <SearchBar
              placeholder="Search anything..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>

          {/* Toolbar Actions */}
          <div className={classes.toolbarActions}>
            {/* Theme Toggle */}
            <IconButton
              className={classes.iconButton}
              onClick={toggleTheme}
              title={darkMode ? "Light Mode" : "Dark Mode"}
            >
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>

            {/* Notifications */}
            {user.id && <NotificationsPopOver />}

            {/* Settings */}
            <IconButton className={classes.iconButton} title="Settings">
              <SettingsIcon />
            </IconButton>

            {/* User Menu */}
            <IconButton onClick={handleMenu} style={{ padding: 4 }}>
              <Avatar className={classes.avatar}>{userInitials}</Avatar>
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant="permanent"
        className={classes.drawer}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div className={classes.drawerHeader}>
          {drawerOpen && (
            <img
              src="https://dzapy.com.br/wp-content/uploads/2025/04/logoBr.png"
              alt="Dzapy"
              className={classes.logo}
            />
          )}
          <IconButton
            onClick={() => setDrawerOpen(!drawerOpen)}
            size="small"
            style={{ marginLeft: drawerOpen ? 0 : "auto" }}
          >
            <MenuIcon />
          </IconButton>
        </div>

        <List style={{ padding: "8px 12px" }}>
          <MainListItems drawerOpen={drawerOpen} />
        </List>
      </Drawer>

      {/* User Modal */}
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      {/* User Menu */}
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        className={classes.userMenu}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          style: {
            marginTop: 8,
            minWidth: 200,
          },
        }}
      >
        <Box px={2} py={1.5} borderBottom="1px solid #E5E7EB">
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            {user?.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {user?.email}
          </Typography>
        </Box>
        <MenuItem onClick={handleOpenUserModal} className={classes.userMenuItem}>
          {i18n.t("mainDrawer.appBar.user.profile")}
        </MenuItem>
        <MenuItem onClick={handleClickLogout} className={classes.userMenuItem}>
          {i18n.t("mainDrawer.appBar.user.logout")}
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <main className={classes.content}>{children}</main>
    </div>
  );
};

export default LoggedInLayout;
